import React, { useEffect, useRef, useState } from "react";
import {
  PrimaryButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Stack,
  IconButton,
  IColumn,
  ConstrainMode,
  CheckboxVisibility,
  Selection,
  DefaultButton,
  Dialog,
  DialogType,
  DialogFooter,
  TooltipHost,
} from "@fluentui/react";
import { useMsal } from "@azure/msal-react";
import {
  getListItems,
  deleteItem,
} from "../../services/graph-service";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { getTokenAndListInfo, japaneseDateConverter, phoneNumFormat } from "../../utils/item-helper";
import { SearchBox } from "@fluentui/react";
import { listname } from "../../constants/global-values";
import { useUser } from "../../utils/user-context";
import { useMediaQuery } from 'react-responsive';
import { common } from "../../messages/common-items";
import { personalInformation } from "../../messages/personal-Information";
import "../../assets/styles/app.css";

export default function ListView() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [sortedItems, setSortedItems] = useState<any[]>([]);
  const [siteId, setSiteId] = useState("");
  const [listId, setListId] = useState("");
  const [sortField, setSortField] = useState<string>("title");
  const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);
  const [columns, setColumns] = useState<IColumn[]>([]);
  const itemsRef = useRef<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [isAnyItemSelected, setIsAnyItemSelected] = useState(false);
  const [hideDialog, setHideDialog] = useState(true);
  const [singleDelete, isSingleDelete] = useState(false);
  const [deleteId, setDeleteId] = useState();
  const [searchText, setSearchText] = useState("");
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { role } = useUser();
  const openDialog = () => setHideDialog(false);
  const closeDialog = () => setHideDialog(true);
  const { Labels: Lbl_Common, SuccessMessages: Success_Common, ErrorMessages: Err_Common } = common;
  const { Labels: Lbl_Personal_Info, SuccessMessages: Success_Personal_Info } = personalInformation;
  const applySortAndSet = (data: any[], field = sortField, descending = isSortedDescending) => {
    const sorted = sortItems(data, field, descending);
    setSortedItems(sorted);
    setColumns(buildColumns(field, descending));
  };
  const calloutStyle = { root: { maxWidth: 240 } };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const userExists = role
        if (userExists === "") {
          navigate('/');
        }
        const { token, siteId, listId } = await getTokenAndListInfo(instance, listname);
        setSiteId(siteId);
        setListId(listId);
        setToken(token);
        const listItems = await getListItems(token, siteId, listId);
        itemsRef.current = listItems;
        applySortAndSet(listItems);
      } catch (error) {
        console.error("Initialization error:", error);
      }
      finally {
        setLoading(false);
      }
    };
    initialize();
  }, [role]);

  const sortItems = (data: any[], field: string, descending: boolean) => {
    return [...data].sort((a, b) => {
      const valA = (a[field] ?? "").toString();
      const valB = (b[field] ?? "").toString();
      return descending
        ? valB.localeCompare(valA)
        : valA.localeCompare(valB);
    });
  };
  const buildColumns = (activeField: string, descending: boolean): IColumn[] => [
    {
      key: "Name",
      name: Lbl_Personal_Info.NAME,
      fieldName: "Name",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "Name",
      isSortedDescending: activeField === "Name" ? descending : false,
      onColumnClick,
      onRender: (item) => {
        return (
          <TooltipHost
            hostClassName="hostStyle"
            content={item.Name}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
          >
            <span
              className="name-link"
              onClick={() => navigate(`/detail/${item.id}`, { state: { from: "list" } })}
            >
              {item.Name}
            </span>
          </TooltipHost>
        );
      },
    },
    {
      key: "DateOfBirth",
      name: Lbl_Personal_Info.DATEOFBIRTH,
      fieldName: "DateOfBirth",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "DateOfBirth",
      isSortedDescending: activeField === "DateOfBirth" ? descending : false,
      onRender: (item) => {
        const japaneseDate = japaneseDateConverter(item.DateOfBirth);
        return (
          <TooltipHost content={`${japaneseDate}`}>
            <span style={{ cursor: 'pointer' }}>
              {japaneseDate}
            </span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "Gender",
      name: Lbl_Personal_Info.GENDER,
      fieldName: "Gender",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "Gender",
      isSortedDescending: activeField === "Gender" ? descending : false,
      onColumnClick,
    },
    {
      key: "Address",
      name: Lbl_Personal_Info.ADDRESS,
      fieldName: "Address",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "Address",
      isSortedDescending: activeField === "Address" ? descending : false,
      onRender: (item) => {
        return (
          <TooltipHost
            content={<span className="tooltip-content">{item.Address}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{item.Address}</span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "PhoneNumber",
      name: Lbl_Personal_Info.PHONENUMBER,
      fieldName: "PhoneNumber",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "PhoneNumber",
      isSortedDescending: activeField === "PhoneNumber" ? descending : false,
      onRender: (item) => {
        const formatNum = item.PhoneNumber ? phoneNumFormat(item.PhoneNumber) : "";
        return (
          <TooltipHost content={`${formatNum}`}>
            <span style={{ cursor: 'pointer' }}>
              {formatNum}
            </span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "EmergencyPhone",
      name: Lbl_Personal_Info.EMERGENCY_PHONENUMBER_LBL,
      fieldName: "EmergencyPhone",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "EmergencyPhone",
      isSortedDescending: activeField === "EmergencyPhone" ? descending : false,

      onRender: (item) => {
        const formatNum = item.EmergencyPhone ? phoneNumFormat(item.EmergencyPhone) : "";
        return (
          <TooltipHost content={`${formatNum}`}>
            <span style={{ cursor: 'pointer' }}>
              {formatNum}
            </span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "EmergencyName",
      name: Lbl_Personal_Info.EMERGENCY_NAME,
      fieldName: "EmergencyName",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "EmergencyName",
      isSortedDescending: activeField === "EmergencyName" ? descending : false,
      onRender: (item) => {
        return (
          <TooltipHost
            content={<span className="tooltip-content">{item.EmergencyName}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{item.EmergencyName}</span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "EmergencyRelation",
      name: Lbl_Personal_Info.EMERGENCY_RELATION,
      fieldName: "EmergencyRelation",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "EmergencyRelation",
      isSortedDescending: activeField === "EmergencyRelation" ? descending : false,
      onRender: (item) => {
        return (
          <TooltipHost
            content={<span className="tooltip-content">{item.EmergencyRelation}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{item.EmergencyRelation}</span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "PartnerCompany",
      name: Lbl_Personal_Info.PARTNER_COMPANY,
      fieldName: "PartnerCompany",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "PartnerCompany",
      isSortedDescending: activeField === "PartnerCompany" ? descending : false,
      onRender: (item) => {
        return (
          <TooltipHost
            content={<span className="tooltip-content">{item.PartnerCompany}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{item.PartnerCompany}</span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "IsSoleProprietor",
      name: Lbl_Personal_Info.IS_SOLE_PROPRIETOR,
      fieldName: "IsSoleProprietor",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "IsSoleProprietor",
      isSortedDescending: activeField === "IsSoleProprietor" ? descending : false,
      onColumnClick,
    },
    {
      key: "SpecialLaborInsurance",
      name: Lbl_Personal_Info.SPECIAL_LABOR_INSURANCE,
      fieldName: "SpecialLaborInsurance",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "SpecialLaborInsurance",
      isSortedDescending: activeField === "SpecialLaborInsurance" ? descending : false,
      onColumnClick,
    },
    {
      key: "BloodType",
      name: Lbl_Personal_Info.BLOOD_TYPE,
      fieldName: "BloodType",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "BloodType",
      isSortedDescending: activeField === "BloodType" ? descending : false,
      onColumnClick,
    },
    {
      key: "HealthCheckDate",
      name: Lbl_Personal_Info.HEALTH_CHECK_DATE,
      fieldName: "HealthCheckDate",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "HealthCheckDate",
      isSortedDescending: activeField === "HealthCheckDate" ? descending : false,
      onRender: (item) => {
        const japaneseDate = japaneseDateConverter(item.HealthCheckDate);
        return (
          <TooltipHost content={`${japaneseDate}`}>
            <span style={{ cursor: 'pointer' }}>
              {japaneseDate}
            </span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "JobType",
      name: Lbl_Personal_Info.JOB_TYPE,
      fieldName: "JobType",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "JobType",
      isSortedDescending: activeField === "JobType" ? descending : false,
      onRender: (item) => {
        return (
          <TooltipHost
            content={<span className="tooltip-content">{item.JobType}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{item.JobType}</span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "Qualifications",
      name: Lbl_Personal_Info.QUALIFICATIONS,
      fieldName: "Qualifications",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "Qualifications",
      isSortedDescending: activeField === "Qualifications" ? descending : false,
      onColumnClick,
      onRender: (item) => {
        const qualifications = Array.isArray(item.Qualifications)
          ? item.Qualifications.join(", ")
          : item.Qualifications;
        return (
          <TooltipHost
            content={<span className="tooltip-content">{qualifications}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{qualifications}</span>
          </TooltipHost>
        );
      }
    },
    {
      key: "QualificationExpiry",
      name: Lbl_Personal_Info.QUALIFICATION_EXPIRY,
      fieldName: "QualificationExpiry",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "QualificationExpiry",
      isSortedDescending: activeField === "QualificationExpiry" ? descending : false,
      onRender: (item) => {
        const japaneseDate = japaneseDateConverter(item.QualificationExpiry);
        return (
          <TooltipHost content={`${japaneseDate}`}>
            <span style={{ cursor: 'pointer' }}>
              {japaneseDate}
            </span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "CertificatePhotos",
      name: Lbl_Personal_Info.QUALIFICATION_PHOTO_LBL,
      fieldName: "CertificatePhotos",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "CertificatePhotos",
      isSortedDescending: activeField === "CertificatePhotos" ? descending : false,
      onRender: (item) => {
        return (
          <TooltipHost
            content={<span className="tooltip-content">{item.CertificatePhotos}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{item.CertificatePhotos}</span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "FacePhotos",
      name: Lbl_Personal_Info.FACE_PHOTOS_LBL,
      fieldName: "FacePhotos",
      minWidth: 100,
      isResizable: true,
      isSorted: activeField === "FacePhotos",
      isSortedDescending: activeField === "FacePhotos" ? descending : false,
      onRender: (item) => {
        return (
          <TooltipHost
            content={<span className="tooltip-content">{item.FacePhotos}</span>}
            calloutProps={{ gapSpace: 0, styles: calloutStyle }}
            hostClassName="hostStyle"
          >
            <span className="tooltip-trigger">{item.FacePhotos}</span>
          </TooltipHost>
        );
      },
      onColumnClick,
    },
    {
      key: "col5",
      name: Lbl_Personal_Info.ACTION,
      minWidth: 120,
      onRender: (item: any) => (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <IconButton
            iconProps={{ iconName: "Edit" }}
            onClick={() => navigate(`/edit/${item.id}`)}
          />
          <IconButton
            iconProps={{ iconName: "Delete" }}
            //onClick={() => handleDelete(item.id)}
            onClick={() => {
              isSingleDelete(true);
              openDialog();
              setDeleteId(item.id);
            }}
          />
        </Stack>
      ),
    },
  ];
  const onColumnClick = (
    ev?: React.MouseEvent<HTMLElement>,
    column?: IColumn
  ) => {
    if (!column || !column.fieldName) return;
    const field = column.fieldName;
    const descending = column.isSorted ? !column.isSortedDescending : false;
    setSearchText("");
    const sorted = sortItems(itemsRef.current, field, descending);
    setSortedItems(sorted);
    setSortField(field);
    setIsSortedDescending(descending);
    setColumns(buildColumns(field, descending));
  };
  const handleDelete = async (id: string) => {
    await deleteItem(token, siteId, listId, id);
    const listItems = await getListItems(token, siteId, listId);
    itemsRef.current = listItems;
    applySortAndSet(listItems);
    closeDialog();
  };
  const onConfirmDelete = () => {
    const selectedItems_List = selectionRef.current.getSelection() as any[];
    selectedItems_List.forEach(item => {
      handleDelete(item.id);
    });
  };
  const selectionRef = useRef(
    new Selection({
      onSelectionChanged: () => {
        const newSelection = selectionRef.current.getSelection() as any[];
        setSelectedItems(newSelection);
        setColumns(buildColumns("null", false));
        setIsAnyItemSelected(newSelection.length > 0);
      },
    })
  );
  const filterItems = (text: string) => {
    setSearchText(text);
    const lowerText = text.toLowerCase();
    const filtered = itemsRef.current.filter((item) => {
      return (
        String(item.Name || "").toLowerCase().includes(lowerText) ||
        String(item.Gender || "").toLowerCase().includes(lowerText) ||
        String(item.Address || "").toLowerCase().includes(lowerText) ||
        String(item.PhoneNumber || "").toLowerCase().includes(lowerText) ||
        String(item.EmergencyName || "").toLowerCase().includes(lowerText) ||
        String(item.JobType || "").toLowerCase().includes(lowerText) ||
        (Array.isArray(item.Qualifications)
          ? item.Qualifications.join(", ").toLowerCase().includes(lowerText)
          : String(item.Qualifications || "").toLowerCase().includes(lowerText))
      );
    });
    applySortAndSet(filtered);
  };

  return (
    <div className="background  list-background">
      <div className="form-container list-box">
        {loading ? (
          <div className="loadercommon-container">
            <Spinner label={Success_Common.LOADER_MSG} size={SpinnerSize.large} />
          </div>
        ) : (
          <>
            <h2>{Lbl_Personal_Info.LIST}</h2>
            <Stack horizontal
              wrap={isMobile}
              tokens={{ childrenGap: 20 }}>
              <PrimaryButton
                className="custom-primary-button"
                text={Lbl_Common.NEWITEM}
                onClick={() => {
                  navigate(`/adduser`, { state: { from: "list" } });
                }}
              />
              <SearchBox
                placeholder={Success_Personal_Info.SEARCH}
                value={searchText}
                onChange={(_, newValue) => filterItems(newValue || "")}
                onClear={() => filterItems("")}
                styles={{
                  root: {
                    width: 300,
                    marginTop: 10,
                    marginLeft: isMobile ? 10 : 0,
                  },
                }}
              />
              {selectedItems.length === 1 && (
                <DefaultButton
                  iconProps={{ iconName: 'Edit' }}
                  text={Lbl_Common.EDIT}
                  className="edit-button"
                  onClick={() => navigate(`/edit/${selectedItems[0].id}`)}
                />
              )}
              {selectedItems.length > 1 && isAnyItemSelected && (
                <DefaultButton
                  iconProps={{ iconName: 'Edit' }}
                  text={Lbl_Common.EDIT}
                  className="edit-button"
                  onClick={() => {
                    navigate(`/bulkedit`, { state: { mode: "bulk-edit", selectedItems } });
                  }}
                />
              )}
              {isAnyItemSelected && (
                <DefaultButton
                  iconProps={{ iconName: 'Delete' }}
                  text={Lbl_Common.DELETE}
                  className="delete-button"
                  onClick={openDialog}
                />
              )}
              <Dialog
                hidden={hideDialog}
                onDismiss={closeDialog}
                dialogContentProps={{
                  type: DialogType.normal,
                  title: Lbl_Common.DELETE,
                  subText: Success_Common.DELETE_CONFIRMATION_MSG
                }}
                modalProps={{
                  isBlocking: true,
                }}
              >
                <DialogFooter>
                  <PrimaryButton
                    onClick={() => {
                      if (singleDelete) {
                        if (deleteId) {
                          handleDelete(deleteId);
                        }
                      }
                      else {
                        onConfirmDelete();
                      }
                    }}
                    text={Lbl_Common.DELETE}
                    styles={{
                      root: {
                        backgroundColor: '#d13438',
                        border: 'none'
                      }
                    }}
                  />
                  <DefaultButton onClick={closeDialog} text={Lbl_Common.CANCEL} />
                </DialogFooter>
              </Dialog>
            </Stack>
            <DetailsList
              layoutMode={DetailsListLayoutMode.fixedColumns}
              selectionMode={SelectionMode.multiple}
              selection={selectionRef.current}
              checkboxVisibility={CheckboxVisibility.always}
              constrainMode={ConstrainMode.unconstrained}
              items={sortedItems}
              columns={columns}
              className="ky-details-list"
            />
          </>
        )}
      </div>
    </div>
  );
}